import { Badge } from 'primereact/badge';
import { TreeNode } from 'primereact/treenode';
import {
  TreeSelect,
  TreeSelectChangeEvent,
  TreeSelectCheckboxSelectionKeyType,
  TreeSelectSelectionKeysType,
} from 'primereact/treeselect';
import { classNames } from 'primereact/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetSupplierCertificatesParams } from '../../../../../../../../hooks/api/certificates';
import { parseMulti } from '../../../../../../../../shared/helpers/url-parameter-parser';
import {
  CertificatePermissionReviewStatus,
  RequestReviewStatus,
} from '../../../../../../../../shared/interfaces/ICertificate';
import styles from './ReviewStatusFilter.module.css';

interface SelectedNode {
  [key: string]: TreeSelectCheckboxSelectionKeyType;
}

// Order and values requested for the Review column filter.
const REVIEW_STATUS_FILTER_OPTIONS: { value: string; labelKey: string }[] = [
  { value: RequestReviewStatus.PENDING, labelKey: 'pending' },
  { value: CertificatePermissionReviewStatus.ACCEPTED, labelKey: 'accepted' },
  { value: CertificatePermissionReviewStatus.REJECTED, labelKey: 'rejected' },
  { value: RequestReviewStatus.REQUESTED, labelKey: 'requested' },
];

interface ReviewStatusFilterProps extends React.ComponentProps<'div'> {
  onReviewStatusFilterChange: (statuses: string[]) => void;
  filters: Partial<GetSupplierCertificatesParams>;
}

const ReviewStatusFilter = ({ onReviewStatusFilterChange, filters }: ReviewStatusFilterProps) => {
  const { t } = useTranslation();
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState<SelectedNode>({});

  const placeholder = t(
    'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.review',
  );

  const translatedStatuses = useMemo(() => {
    return REVIEW_STATUS_FILTER_OPTIONS.map((option) => {
      return {
        name: t(`certificatesPage.tabs.supplierCertificatesTab.reviewStatuses.${option.labelKey}`),
        code: option.value,
      };
    });
  }, [t]);

  // Based on the filter values that initially come from the parent component (from URL actually), set the selected nodes
  useEffect(() => {
    prepareInitialNodeKeys();
  }, []);

  // Convert your flat status to TreeNode array
  const treeNodes: TreeNode[] = translatedStatuses.map((status) => {
    return {
      key: status.code.toString(),
      label: status.name,
    };
  });

  const prepareInitialNodeKeys = (): void => {
    // Get the array of status keys from parent, i.e. the URL
    const statusKeys = parseMulti(filters.reviewStatus);

    // Map to store the selected nodes to be set in the state, to show them selected in the tree
    const preSelectedNodeKeys: SelectedNode = {};

    statusKeys.forEach((statusKey: string) => {
      preSelectedNodeKeys[statusKey] = { checked: true, partialChecked: false };
    });
    // Now highlight those nodes in the tree
    setSelectedStatus(preSelectedNodeKeys);
  };

  const handleChange = (e: TreeSelectChangeEvent) => {
    const selectedKeysMap = (e.value as SelectedNode) || {};

    setSelectedStatus(selectedKeysMap);

    // Map back to your original object structure
    const selectedItems: string[] = [];

    Object.keys(selectedKeysMap).forEach((key) => {
      selectedItems.push(key);
    });

    onReviewStatusFilterChange(selectedItems);
  };

  const valueTemplate = (nodes: TreeNode[] | TreeNode | null | undefined) => {
    // If nothing is selected, show the placeholder
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return <span className='line-height-2'>{placeholder}</span>;
    }

    // Display the number of selected nodes
    return (
      <div className='flex flex-row gap-1 line-height-2'>
        <span className='text-primary'>{placeholder}:</span>
        <Badge className='flex align-items-center justify-content-center' value={nodes.length} />
      </div>
    );
  };

  // Custom template for items
  const nodeTemplate = (node: TreeNode) => {
    return (
      <div className='flex-1 w-full p-2'>
        <div className='flex flex-row justify-content-between align-items-center'>
          <span className={'font-normal break-all'}>{node.label}</span>
        </div>
      </div>
    );
  };

  return (
    <TreeSelect
      value={selectedStatus as TreeSelectSelectionKeysType}
      options={treeNodes}
      selectionMode='multiple'
      valueTemplate={valueTemplate}
      nodeTemplate={nodeTemplate}
      onChange={handleChange}
      onHide={() => {
        setIsOverlayVisible(false);
      }}
      showClear
      filterInputAutoFocus
      onShow={() => {
        setIsOverlayVisible(true);
      }}
      className={classNames(
        styles['review-status-filter'],
        isOverlayVisible
          ? styles['review-status-filter-open']
          : styles['review-status-filter-close'],
        'border-none',
      )}
      pt={{
        root: {
          className: styles['root'],
        },
        label: {
          className: styles['label'],
        },
        closeButton: {
          className: 'hidden',
        },
        wrapper: {
          className: styles['review-status-filter'],
        },
        header: {
          className: styles['header'],
        },
        clearIcon: {
          className: classNames(
            'text-black-alpha-60',
            {
              hidden: selectedStatus === undefined || Object.keys(selectedStatus).length === 0,
            },
            styles['clear-icon'],
          ),
        },
        trigger: {
          className: classNames({
            'hidden w-0 p-0 overflow-hidden shrink-0':
              selectedStatus && Object.keys(selectedStatus).length > 0,
          }),
        },
      }}
    />
  );
};

export default ReviewStatusFilter;
